import React from "react";
import { Tooltip, makeStyles, shorthands, tokens } from "@fluentui/react-components";
import {
  TextBulletListSquare24Regular,
  MailEdit24Regular,
  TextGrammarCheckmark24Regular,
  Wand24Regular,
} from "@fluentui/react-icons";
import { FolderSuggestButton } from "./FolderSuggestButton";
import { TagSuggestButton } from "./TagSuggestButton";
import type { FolderSuggestion, TagSuggestion } from "../suggestions";
import type { MailFolder, MasterCategory } from "../mailbox-adapter";

interface QuickActionsProps {
  /** Whether an email is currently open — drives enablement and the tooltip. */
  hasEmail: boolean;
  onAction: (prompt: string) => void;
  disabled: boolean;
  /** Folder move / tag apply need Graph (same requirement as every other
   * folder/label write in this app) — disables those two buttons when false. */
  graphConnected: boolean;
  onSuggestFolder: () => Promise<{ suggestions: FolderSuggestion[]; existing: MailFolder[] }>;
  onMoveToFolder: (folderName: string) => void;
  onSuggestTags: () => Promise<{ suggestions: TagSuggestion[]; existing: MasterCategory[] }>;
  onApplyTags: (tagNames: string[]) => Promise<void>;
  /** True when the open item is a compose surface (draft) rather than a
   * received message — swaps the read-oriented actions (Summarize, Draft
   * Reply, Suggest Folder, Suggest Tags) for writing-oriented ones, since
   * none of the read actions apply to an in-progress draft. */
  composeMode: boolean;
}

const ACTIONS = [
  {
    label: "Summarize",
    prompt: "Please summarize the current email in concise bullet points.",
    Icon: TextBulletListSquare24Regular,
  },
  {
    label: "Draft Reply",
    prompt: "Please draft a professional reply to this email.",
    Icon: MailEdit24Regular,
  },
] as const;

const COMPOSE_ACTIONS = [
  {
    label: "Proofread",
    prompt:
      "Please proofread this draft: point out any grammar, spelling, or clarity issues, and suggest corrected text.",
    Icon: TextGrammarCheckmark24Regular,
  },
  {
    label: "Improve",
    prompt:
      "Please suggest ways to improve this draft's tone, clarity, and impact, and propose a revised version.",
    Icon: Wand24Regular,
  },
] as const;

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    ...shorthands.gap("6px"),
  },
  card: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("5px", "8px"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    transitionProperty: "box-shadow, border-color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      boxShadow: tokens.shadow4,
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
    ":disabled": {
      cursor: "not-allowed",
      boxShadow: "none",
      opacity: 0.5,
    },
  },
  icon: {
    color: tokens.colorBrandForeground1,
    fontSize: "16px",
    flexShrink: 0,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

export function QuickActions({
  hasEmail,
  onAction,
  disabled,
  graphConnected,
  onSuggestFolder,
  onMoveToFolder,
  onSuggestTags,
  onApplyTags,
  composeMode,
}: QuickActionsProps) {
  const styles = useStyles();
  const isDisabled = disabled || !hasEmail;
  const suggestDisabled = isDisabled || !graphConnected;
  const suggestDisabledReason = !hasEmail
    ? "Open an email first"
    : !graphConnected
      ? "Connect your account first"
      : undefined;
  const actions = composeMode ? COMPOSE_ACTIONS : ACTIONS;

  return (
    <div className={styles.grid}>
      {actions.map(({ label, prompt, Icon }) => (
        <Tooltip
          key={label}
          content={hasEmail ? label : "Open an email first"}
          relationship="label"
        >
          <button
            type="button"
            className={styles.card}
            disabled={isDisabled}
            onClick={() => onAction(prompt)}
          >
            <Icon className={styles.icon} aria-hidden />
            <span className={styles.label}>{label}</span>
          </button>
        </Tooltip>
      ))}
      {!composeMode && (
        <>
          <FolderSuggestButton
            disabled={suggestDisabled}
            disabledReason={suggestDisabledReason}
            className={styles.card}
            iconClassName={styles.icon}
            labelClassName={styles.label}
            onSuggest={onSuggestFolder}
            onSelect={onMoveToFolder}
          />
          <TagSuggestButton
            disabled={suggestDisabled}
            disabledReason={suggestDisabledReason}
            className={styles.card}
            iconClassName={styles.icon}
            labelClassName={styles.label}
            onSuggest={onSuggestTags}
            onApply={onApplyTags}
          />
        </>
      )}
    </div>
  );
}
